import React, { useEffect, useMemo, useRef, useState } from "react";
import { doc, setDoc, addDoc, collection, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import { colors, radius, spacing, typography } from "../../utils/designTokens";
import { useUser } from "../../context/UserContext";
import { useToast } from "../../components/Toast";
import { DEFAULT_DECK, TIPO_DECK, buildDeckHtml } from "../../utils/deckQueEs";
import { descargarDeckPdf, descargarDeckPngs, inlineDeckAssets } from "../../utils/deckExport";
import {
  LS_CONTACTO, loadContacto, Label, SecTitle, input, topbarStyle, tabsRow, tabBtn, backBtn,
  primaryBtn, ghostBtn, miniBtn, descMenu, descItem, infoBanner, boxDest, boxContacto,
} from "./materialesUi";
import {
  FaArrowLeft, FaSave, FaTrash, FaDownload, FaChevronDown, FaRegFilePdf, FaRegImage,
  FaCode, FaPen, FaEye, FaPlay,
} from "react-icons/fa";

const clone = (x) => JSON.parse(JSON.stringify(x));

/**
 * Editor del deck "Qué es Habitia" (tipo deck_que_es). Mismo layout que el
 * editor de flyers: preview dominante (iframe con el deck navegable) + panel
 * lateral con los textos de cada slide. "Presentar" pone el deck en pantalla
 * completa para mostrarlo en una reunión.
 */
export default function DeckEditor({ material, isMobile, isSuperAdmin, myEmail, onClose, onSaved, onDeleted }) {
  const { showToast } = useToast();
  const { userName, userEmail } = useUser();
  const email = userEmail || auth.currentUser?.email || "";

  const esGeneral = (material.scope || "general") !== "personal";
  const esBuiltin = !!material.builtin;
  const docId = esBuiltin ? null : material.id;
  const copiaAMios = esGeneral && !isSuperAdmin && (esBuiltin || !!docId);
  const puedeEliminar = !!docId && (isSuperAdmin || !esGeneral);

  const c0 = loadContacto(email);
  const [nombre, setNombre] = useState(material.nombre || "");
  const [deck, setDeck] = useState(() => clone(material.deck || DEFAULT_DECK));
  const [agenteName, setAgenteName] = useState(c0.name || userName || "");
  const [agenteEmail, setAgenteEmail] = useState(c0.email || email);
  const [agenteTel, setAgenteTel] = useState(c0.tel || "");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("preview"); // mobile: editar | preview
  const [descOpen, setDescOpen] = useState(false);
  const [exportando, setExportando] = useState(false);
  const iframeRef = useRef(null);
  const slideRef = useRef(0); // slide activo en el preview (via postMessage)

  useEffect(() => { if (userName && !agenteName) setAgenteName(userName); }, [userName]); // eslint-disable-line
  useEffect(() => { if (email && !agenteEmail) setAgenteEmail(email); }, [email]); // eslint-disable-line
  useEffect(() => {
    try { localStorage.setItem(LS_CONTACTO(email), JSON.stringify({ name: agenteName, email: agenteEmail, tel: agenteTel })); } catch (_) {}
  }, [agenteName, agenteEmail, agenteTel, email]);

  // El deck avisa por postMessage en qué slide está; al regenerar el preview
  // se arranca en ese mismo slide para no volver al 1 con cada edición.
  useEffect(() => {
    const fn = (e) => {
      const m = e && e.data;
      if (m && m.type === "habitia-deck-slide" && typeof m.index === "number") slideRef.current = m.index;
    };
    window.addEventListener("message", fn);
    return () => window.removeEventListener("message", fn);
  }, []);

  const agente = useMemo(
    () => ({ name: agenteName, email: agenteEmail, tel: agenteTel }),
    [agenteName, agenteEmail, agenteTel]
  );
  const [previewHtml, setPreviewHtml] = useState(() => buildDeckHtml({ deck, agente }));
  useEffect(() => {
    const t = setTimeout(() => setPreviewHtml(buildDeckHtml({ deck, agente, startSlide: slideRef.current })), 300);
    return () => clearTimeout(t);
  }, [deck, agente]);

  // ── Updaters del contenido ──
  const setS1 = (patch) => setDeck((d) => ({ ...d, s1: { ...d.s1, ...patch } }));
  const setS2 = (patch) => setDeck((d) => ({ ...d, s2: { ...d.s2, ...patch } }));
  const setS3 = (patch) => setDeck((d) => ({ ...d, s3: { ...d.s3, ...patch } }));
  const setChip = (i, v) => setDeck((d) => ({ ...d, s1: { ...d.s1, chips: d.s1.chips.map((c, j) => (j === i ? v : c)) } }));
  const setCard = (i, patch) => setDeck((d) => ({ ...d, s2: { ...d.s2, cards: d.s2.cards.map((c, j) => (j === i ? { ...c, ...patch } : c)) } }));
  const setRazon = (i, patch) => setDeck((d) => ({ ...d, s3: { ...d.s3, razones: d.s3.razones.map((r, j) => (j === i ? { ...r, ...patch } : r)) } }));
  const setDemo = (patch) => setDeck((d) => ({ ...d, s3: { ...d.s3, demo: { ...d.s3.demo, ...patch } } }));
  const restaurar = () => setDeck(clone(DEFAULT_DECK));

  async function guardar() {
    if (!nombre.trim()) { showToast("Poné un nombre al material.", "error"); return; }
    setSaving(true);
    try {
      let scope, owner, id;
      if (copiaAMios) { scope = "personal"; owner = myEmail; id = null; }
      else if (docId) {
        scope = material.scope || (esGeneral ? "general" : "personal");
        owner = scope === "personal" ? (material.owner || myEmail) : "";
        id = docId;
      } else {
        scope = material.scope || (isSuperAdmin ? "general" : "personal");
        owner = scope === "personal" ? myEmail : "";
        id = null;
      }
      const payload = { nombre: nombre.trim(), tipo: TIPO_DECK, deck, scope, owner, updatedAt: serverTimestamp(), updatedBy: email };
      if (!id) {
        payload.createdAt = serverTimestamp();
        payload.createdBy = email;
        const ref = await addDoc(collection(db, "materiales"), payload);
        id = ref.id;
      } else {
        await setDoc(doc(db, "materiales", id), payload, { merge: true });
      }
      showToast(copiaAMios ? "Guardado en Mis materiales." : "Material guardado.", "success");
      onSaved(id, { ...payload });
    } catch (e) {
      showToast(e.message || "No se pudo guardar.", "error");
    }
    setSaving(false);
  }

  async function eliminar() {
    if (!docId) { onClose(); return; }
    // eslint-disable-next-line no-alert
    if (!window.confirm("¿Eliminar este material?")) return;
    try { await deleteDoc(doc(db, "materiales", docId)); showToast("Material eliminado.", "success"); onDeleted(docId); }
    catch (e) { showToast(e.message || "No se pudo eliminar.", "error"); }
  }

  async function presentar() {
    const el = iframeRef.current;
    if (el && el.requestFullscreen) {
      try {
        await el.requestFullscreen();
        el.contentWindow?.focus(); // que las flechas del teclado naveguen el deck
        return;
      } catch (_) { /* cae al fallback */ }
    }
    // Fallback (p. ej. iPhone): abrir el deck autocontenido en otra pestaña.
    try {
      const standalone = await inlineDeckAssets(buildDeckHtml({ deck, agente }));
      const url = URL.createObjectURL(new Blob([standalone], { type: "text/html;charset=utf-8" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (_) {
      showToast("No se pudo abrir la presentación.", "error");
    }
  }

  const slugName = () => (nombre || "que-es").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "que-es";

  async function doExport(tipo) {
    setDescOpen(false);
    setExportando(true);
    try {
      const base = `habitia-deck-${slugName()}`;
      if (tipo === "pdf") {
        await descargarDeckPdf(buildDeckHtml({ deck, agente, mode: "export" }), `${base}.pdf`);
        showToast("PDF descargado. Listo para compartir o proyectar.", "success");
      } else if (tipo === "img") {
        await descargarDeckPngs(buildDeckHtml({ deck, agente, mode: "export" }), base);
        showToast("Slides descargados como PNG.", "success");
      } else {
        const standalone = await inlineDeckAssets(buildDeckHtml({ deck, agente }));
        const url = URL.createObjectURL(new Blob([standalone], { type: "text/html;charset=utf-8" }));
        const a = document.createElement("a"); a.href = url; a.download = `${base}.html`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        showToast("Deck HTML descargado (se abre en cualquier navegador).", "success");
      }
    } catch (e) {
      showToast("No se pudo generar el archivo. Probá de nuevo.", "error");
    }
    setExportando(false);
  }

  const descargarBtn = (
    <div style={{ position: "relative" }}>
      <button onClick={() => setDescOpen((o) => !o)} disabled={exportando} style={ghostBtn} aria-haspopup="menu" aria-expanded={descOpen}>
        <FaDownload size={12} /> {exportando ? "Generando…" : "Descargar"} <FaChevronDown size={9} />
      </button>
      {descOpen && (
        <>
          <div onClick={() => setDescOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div role="menu" style={descMenu}>
            <button onClick={() => doExport("pdf")} style={descItem}><FaRegFilePdf size={12} color={colors.error} /> PDF (3 slides)</button>
            <button onClick={() => doExport("img")} style={descItem}><FaRegImage size={12} color={colors.primary} /> Imágenes (PNG)</button>
            <button onClick={() => doExport("html")} style={descItem}><FaCode size={12} color={colors.gray600} /> HTML interactivo</button>
          </div>
        </>
      )}
    </div>
  );

  const textarea = { ...input, resize: "vertical", fontFamily: typography.fontFamily };
  const hint = { margin: "6px 0 0", fontSize: typography.sizes.xs, color: colors.gray600 };

  const editorPanel = (
    <div style={{ display: "grid", gap: spacing[3] }}>
      {copiaAMios && (
        <div style={infoBanner}>Deck <b>general</b> de Habitia. Al guardar se crea <b>tu copia</b> en “Mis materiales” (renombrala como quieras).</div>
      )}
      {esBuiltin && isSuperAdmin && (
        <div style={infoBanner}>Plantilla base del deck. Al guardar se crea la versión <b>general</b> editable que ven todos los comerciales.</div>
      )}
      <p style={{ ...hint, margin: 0 }}>En los textos podés usar <b>*palabra*</b> para resaltarla (azul en los títulos).</p>

      <section style={boxContacto}>
        <SecTitle color={colors.gray700}>Tu contacto — Agente Comercial</SecTitle>
        <Label>Nombre</Label>
        <input value={agenteName} onChange={(e) => setAgenteName(e.target.value)} placeholder="Nombre y apellido" style={input} />
        <div style={{ height: spacing[2] }} />
        <Label>Email</Label>
        <input value={agenteEmail} onChange={(e) => setAgenteEmail(e.target.value)} placeholder="vos@habitia.ai" style={input} />
        <div style={{ height: spacing[2] }} />
        <Label>Teléfono</Label>
        <input value={agenteTel} onChange={(e) => setAgenteTel(e.target.value)} placeholder="+598 9x xxx xxx" style={input} />
        <p style={hint}>Aparece en la tarjeta de demo del slide 3.</p>
      </section>

      <section style={boxDest}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: spacing[2] }}>
          <SecTitle color={colors.primary} style={{ marginBottom: 0 }}>Slide 1 — Qué es</SecTitle>
          <button onClick={restaurar} style={miniBtn}>Restaurar textos</button>
        </div>
        <Label>Título</Label>
        <textarea value={deck.s1.titulo} onChange={(e) => setS1({ titulo: e.target.value })} rows={2} style={textarea} />
        <div style={{ height: spacing[2] }} />
        <Label>Texto principal</Label>
        <textarea value={deck.s1.lead} onChange={(e) => setS1({ lead: e.target.value })} rows={4} style={textarea} />
        <div style={{ height: spacing[2] }} />
        <Label>Chips</Label>
        <div style={{ display: "grid", gap: 6 }}>
          {deck.s1.chips.map((c, i) => (
            <input key={i} value={c} onChange={(e) => setChip(i, e.target.value)} style={input} aria-label={`Chip ${i + 1}`} />
          ))}
        </div>
      </section>

      <section style={boxDest}>
        <SecTitle color={colors.primary}>Slide 2 — Diferenciales</SecTitle>
        <Label>Título</Label>
        <input value={deck.s2.titulo} onChange={(e) => setS2({ titulo: e.target.value })} style={input} />
        <div style={{ height: spacing[2] }} />
        <Label>Bajada</Label>
        <input value={deck.s2.subhead} onChange={(e) => setS2({ subhead: e.target.value })} style={input} />
        <div style={{ height: spacing[2] }} />
        <div style={{ display: "grid", gap: spacing[2] }}>
          {deck.s2.cards.map((c, i) => (
            <div key={i} style={{ border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, padding: spacing[2], background: colors.white }}>
              <Label>{c.destacada ? `Card ${i + 1} (destacada)` : `Card ${i + 1}`}</Label>
              {c.destacada && (
                <>
                  <input value={c.tag || ""} onChange={(e) => setCard(i, { tag: e.target.value })} placeholder="Etiqueta (ej. Con IA)" style={{ ...input, marginBottom: 6 }} />
                </>
              )}
              <input value={c.titulo} onChange={(e) => setCard(i, { titulo: e.target.value })} placeholder="Título" style={{ ...input, marginBottom: 6 }} />
              <textarea value={c.desc} onChange={(e) => setCard(i, { desc: e.target.value })} placeholder="Descripción" rows={2} style={textarea} />
            </div>
          ))}
        </div>
      </section>

      <section style={boxDest}>
        <SecTitle color={colors.primary}>Slide 3 — Por qué elegirla</SecTitle>
        <Label>Título</Label>
        <textarea value={deck.s3.titulo} onChange={(e) => setS3({ titulo: e.target.value })} rows={2} style={textarea} />
        <div style={{ height: spacing[2] }} />
        <div style={{ display: "grid", gap: spacing[2] }}>
          {deck.s3.razones.map((r, i) => (
            <div key={i} style={{ border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, padding: spacing[2], background: colors.white }}>
              <Label>{`Razón ${i + 1}`}</Label>
              <input value={r.titulo} onChange={(e) => setRazon(i, { titulo: e.target.value })} placeholder="Título" style={{ ...input, marginBottom: 6 }} />
              <textarea value={r.desc} onChange={(e) => setRazon(i, { desc: e.target.value })} placeholder="Descripción" rows={2} style={textarea} />
            </div>
          ))}
        </div>
        <div style={{ height: spacing[2] }} />
        <Label>Tarjeta de demo</Label>
        <div style={{ display: "grid", gap: 6 }}>
          <input value={deck.s3.demo.kicker} onChange={(e) => setDemo({ kicker: e.target.value })} placeholder="Kicker (ej. Probá Habitia)" style={input} />
          <input value={deck.s3.demo.titulo} onChange={(e) => setDemo({ titulo: e.target.value })} placeholder="Título" style={input} />
          <textarea value={deck.s3.demo.texto} onChange={(e) => setDemo({ texto: e.target.value })} placeholder="Texto junto al QR" rows={2} style={textarea} />
          <input value={deck.s3.demo.cta} onChange={(e) => setDemo({ cta: e.target.value })} placeholder="Botón (CTA)" style={input} />
          <input value={deck.s3.demo.lugar} onChange={(e) => setDemo({ lugar: e.target.value })} placeholder="Ubicación (ej. Montevideo, Uruguay)" style={input} />
        </div>
      </section>
    </div>
  );

  const previewPane = (
    <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto", background: "#E9EBEE", padding: isMobile ? 10 : spacing[4] }}>
      <div style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", borderRadius: radius.md, overflow: "hidden", boxShadow: "0 12px 34px rgba(0,0,0,0.22)", background: "#0b0d10" }}>
        <iframe
          ref={iframeRef} title="Vista previa del deck" srcDoc={previewHtml} allowFullScreen
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, display: "block" }}
        />
      </div>
      <p style={{ margin: "10px 2px 0", fontSize: typography.sizes.xs, color: colors.gray600, textAlign: "center" }}>
        Navegá con ‹ › o las flechas del teclado · “Presentar” lo lleva a pantalla completa
      </p>
    </div>
  );

  const presentarBtn = (
    <button onClick={presentar} style={ghostBtn} aria-label="Presentar en pantalla completa"><FaPlay size={11} /> {isMobile ? "" : "Presentar"}</button>
  );

  const topbar = (
    <div style={topbarStyle}>
      <button onClick={onClose} style={backBtn} aria-label="Volver a materiales"><FaArrowLeft size={12} /> {isMobile ? "" : "Materiales"}</button>
      <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre del material" style={{ ...input, flex: 1, minWidth: 0, height: 38, background: colors.gray50 }} />
      <button onClick={guardar} disabled={saving} style={primaryBtn}><FaSave size={12} /> {saving ? "…" : (copiaAMios ? "Guardar copia" : "Guardar")}</button>
      {!isMobile && presentarBtn}
      {!isMobile && descargarBtn}
      {!isMobile && puedeEliminar && <button onClick={eliminar} style={{ ...ghostBtn, color: colors.error }} aria-label="Eliminar"><FaTrash size={12} /></button>}
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        {topbar}
        <div style={tabsRow}>
          <button onClick={() => setTab("editar")} style={tabBtn(tab === "editar")}><FaPen size={11} /> Editar</button>
          <button onClick={() => setTab("preview")} style={tabBtn(tab === "preview")}><FaEye size={11} /> Vista previa</button>
        </div>
        {tab === "editar" ? (
          <div style={{ padding: 14, overflowY: "auto" }}>
            {editorPanel}
            <div style={{ display: "flex", gap: spacing[2], marginTop: spacing[4], alignItems: "center" }}>
              {presentarBtn}
              {descargarBtn}
              {puedeEliminar && <button onClick={eliminar} style={{ ...ghostBtn, color: colors.error }}><FaTrash size={12} /></button>}
            </div>
          </div>
        ) : (
          previewPane
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {topbar}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {previewPane}
        <aside style={{ width: 360, flexShrink: 0, overflowY: "auto", borderLeft: `1px solid ${colors.gray200}`, background: colors.white, padding: spacing[4] }}>
          {editorPanel}
        </aside>
      </div>
    </div>
  );
}
